import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { ModalComponent, ModalConfig } from '../modal/modal';
import { SkeletonComponent } from '../skeleton/skeleton';
import { OrgManagementApi } from '../../services/org-management-api/org-management-api';
import { StateStoreService } from '../../services/state-store/state-store';
import { DocumentContentItemTypeEnum, Prompt } from '../../models';
import { getDocumentTypeLabel } from '../../utils/document.utils';

@Component({
  selector: 'app-prompt-modal',
  imports: [FormsModule, DatePipe, ModalComponent, SkeletonComponent],
  templateUrl: './prompt-modal.html',
  styleUrl: './prompt-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromptModalComponent {
  private readonly orgManagementApi = inject(OrgManagementApi);
  private readonly stateService = inject(StateStoreService);

  readonly isVisible = input(false);
  readonly contentType = input<DocumentContentItemTypeEnum | null>(null);

  readonly closed = output<void>();
  readonly saved = output<void>();

  // Public so tests can manipulate / assert.
  readonly prompt = signal<Prompt | null>(null);
  readonly promptText = signal<string>('');
  readonly isLoading = signal<boolean>(false);
  readonly isSaving = signal<boolean>(false);
  readonly error = signal<string | null>(null);

  protected readonly contentTypeDisplay = computed(() => {
    const ct = this.contentType();
    return ct ? getDocumentTypeLabel(ct) : '';
  });

  readonly hasChanges = computed(() => {
    const p = this.prompt();
    return p ? this.promptText() !== p.prompt_text : false;
  });

  protected readonly modalConfig: ModalConfig = {
    title: 'Prompt Editor',
    size: 'large',
    showLogo: true,
    showCloseButton: true,
    showFooter: true,
    primaryButtonText: 'Save Changes',
    secondaryButtonText: 'Cancel',
  };

  constructor() {
    // Replace ngOnInit + ngOnChanges with a single signal effect: react
    // to (isVisible, contentType) edges declaratively. Opening loads the
    // prompt; closing resets local state.
    effect(() => {
      const visible = this.isVisible();
      const ct = this.contentType();
      if (visible && ct) {
        this.loadPrompt(ct);
      } else if (!visible) {
        this.resetState();
      }
    });
  }

  loadPrompt(contentType: DocumentContentItemTypeEnum | null = this.contentType()): void {
    if (!contentType) {
      this.error.set('Invalid content type for prompt loading');
      return;
    }

    const orgId = this.stateService.selectedOrgId();
    if (!orgId) {
      this.error.set('No organization selected');
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);

    this.orgManagementApi.getPrompts(orgId, contentType).subscribe({
      next: prompts => {
        const prompt = prompts?.[0] ?? this.buildDefaultPrompt(contentType);
        this.prompt.set(prompt);
        this.promptText.set(prompt.prompt_text);
        this.isLoading.set(false);
      },
      error: () => {
        this.error.set('Failed to load prompt');
        this.isLoading.set(false);
      },
    });
  }

  protected onPromptTextChange(event: Event): void {
    this.promptText.set((event.target as HTMLTextAreaElement).value);
  }

  onSave(): void {
    const ct = this.contentType();
    if (!ct) {
      this.error.set('Invalid content type for saving prompt');
      return;
    }
    const orgId = this.stateService.selectedOrgId();
    if (!orgId) {
      this.error.set('No organization selected');
      return;
    }
    const promptText = this.promptText().trim();
    if (!promptText) {
      this.error.set('Prompt text cannot be empty');
      return;
    }
    const prompt = this.prompt();
    if (!prompt) return;

    this.isSaving.set(true);
    this.error.set(null);

    const save$ = prompt.prompt_id.startsWith('default-')
      ? this.orgManagementApi.createPrompt(orgId, ct, promptText)
      : this.orgManagementApi.updatePrompt(orgId, prompt.prompt_id, promptText, ct);

    save$.subscribe({
      next: () => {
        this.isSaving.set(false);
        this.saved.emit();
        this.closed.emit();
      },
      error: () => {
        this.error.set('Failed to save prompt');
        this.isSaving.set(false);
      },
    });
  }

  onCancel(): void {
    this.closed.emit();
  }

  onClose(): void {
    this.closed.emit();
  }

  private resetState(): void {
    this.prompt.set(null);
    this.promptText.set('');
    this.isLoading.set(false);
    this.isSaving.set(false);
    this.error.set(null);
  }

  private buildDefaultPrompt(contentType: DocumentContentItemTypeEnum): Prompt {
    return {
      prompt_id: `default-${contentType}`,
      content_type: contentType,
      prompt_text:
        DEFAULT_PROMPTS[contentType] ??
        'Create a comprehensive document based on the specified content type.',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  }
}

/** Seed text shown when no org-level prompt has been customised yet —
 *  used as the editor's starting point for first-time customisation.
 *
 *  Assessment types (AI Risk, DPIA) are NOT keyed here — per-org prompt
 *  overrides for assessments were removed with VER-284; assessment
 *  prompts are now owned server-side by the ``AssessmentTypePlugin``
 *  (``default_system_prompt``). This map covers documents only. */
const DEFAULT_PROMPTS: Record<DocumentContentItemTypeEnum, string> = {
  [DocumentContentItemTypeEnum.PRIVACY_POLICY]: `Create a comprehensive privacy policy that covers:
    - Data collection practices
    - How personal information is used
    - Data sharing policies
    - User rights and choices
    - Security measures
    - Contact information for privacy concerns

    Ensure compliance with GDPR, CCPA, and other applicable privacy laws.`,

  [DocumentContentItemTypeEnum.DPA]: `Draft a Data Processing Agreement that includes:
    - Purpose and scope of data processing
    - Types of personal data involved
    - Legal basis for processing
    - Data retention periods
    - Security measures and safeguards
    - Data subject rights
    - Breach notification procedures
    - Termination and data return clauses`,

  [DocumentContentItemTypeEnum.TERMS_AND_CONDITIONS]: `Create terms and conditions covering:
    - Service description and availability
    - User obligations and restrictions
    - Intellectual property rights
    - Limitation of liability
    - Dispute resolution procedures
    - Governing law and jurisdiction
    - Modification and termination rights`,

  [DocumentContentItemTypeEnum.COOKIE_POLICY]: `Develop a cookie policy explaining:
    - What cookies are used
    - Types of cookies (essential, functional, analytics, marketing)
    - Purpose of each cookie type
    - How users can manage cookie preferences
    - Third-party cookie usage
    - Cookie retention periods
    - How to opt-out of non-essential cookies`,

  [DocumentContentItemTypeEnum.CUSTOM]: `Create a custom compliance document that includes:
    - The requested scope and purpose
    - Clear obligations and responsibilities
    - Operational procedures and governance
    - Applicable legal and regulatory considerations
    - Review and update expectations`,
};
